import { useMemo, useState } from 'react';
import {
  recheckAchievements,
  useAchievementProgress,
  useAchievements,
  useCurrentStreak,
  useStreakShields,
  type AchievementKind,
  type AchievementRow,
} from '@busy/api';
import { useToast } from '@busy/ui';
import { useT } from '../lib/i18n';
import { ACHIEVEMENT_CATALOG, progressInBand, thresholdLabel } from '../lib/achievements';
import { deriveNickname } from '../lib/nickname';
import { badgeArtworkUrl, nicknameArtworkUrl } from '../lib/badgeArtwork';
import { KindIcon } from '../components/CelebrationModal';
import { AchievementDetailModal } from '../components/AchievementDetailModal';
import { LeaderboardPanel } from '../components/achievements/LeaderboardPanel';
import { NicknameJourneyList } from '../components/achievements/NicknameJourneySection';

const KIND_ORDER: AchievementKind[] = ['streak', 'clients', 'cuts', 'revenue', 'lessons', 'transformations', 'timekeeper'];

// Persists which kind sections the user has expanded so a returning visitor
// sees the same layout they left. Default is empty (all collapsed) — the
// gallery's primary use is checking progress on a specific kind, so opening
// the page short and tapping the one you care about is faster than scrolling
// past five fully-expanded grids.
const EXPAND_STORAGE_KEY = 'busy.achGallery.expanded';
function loadExpanded(): Set<AchievementKind> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(EXPAND_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as AchievementKind[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}
function saveExpanded(s: Set<AchievementKind>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(EXPAND_STORAGE_KEY, JSON.stringify([...s]));
  } catch {
    // localStorage unavailable / quota — silent
  }
}

export function Achievements() {
  const t = useT();
  const toast = useToast();
  const all = useAchievements();
  const earned = all.data ?? [];
  const [refreshing, setRefreshing] = useState(false);
  const [detail, setDetail] = useState<AchievementRow | null>(null);
  const [expandedKinds, setExpandedKinds] = useState<Set<AchievementKind>>(() => loadExpanded());

  function toggleKind(kind: AchievementKind) {
    setExpandedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      saveExpanded(next);
      return next;
    });
  }

  // Lifetime totals to drive progress bars on locked chips. We use
  // the canonical `get_user_achievement_progress` RPC (migration
  // 0064) — the SAME query `check_achievements()` runs to decide
  // what to award. Single source of truth means the progress bars
  // here always match the DB's awarding logic exactly, including
  // every anti-gaming rule (6-min floor, 20/day cap, $500/visit
  // revenue cap, 120-min timekeeper cap, Anonymous exclusion,
  // original_price_cents ratcheting from 0059/0060).
  //
  // Replaces an earlier local re-implementation that drifted from
  // the DB function in two ways: it read price_cents (not the
  // post-0059 original_price_cents ratchet) and skipped the
  // 20-visits-per-UTC-day cap. Both fixed by deleting the local
  // computation and using the RPC.
  const progress = useAchievementProgress();
  const currentByKind: Record<AchievementKind, number> = useMemo(() => {
    const p = progress.data;
    return {
      streak:          p?.streak ?? 0,
      clients:         p?.clients ?? 0,
      cuts:            p?.cuts ?? 0,
      revenue:         p?.revenue_cents ?? 0,
      lessons:         p?.lessons ?? 0,
      transformations: p?.transformations ?? 0,
      timekeeper:      p?.timekeeper_hours ?? 0,
    };
  }, [progress.data]);

  async function handleRefresh() {
    setRefreshing(true);
    const { error, data } = await recheckAchievements();
    setRefreshing(false);
    if (error) {
      toast(t('ach.gallery.refreshError', { message: error.message }), 'error');
      return;
    }
    const newCount = (data ?? []).length;
    toast(
      newCount === 0
        ? t('ach.gallery.refreshNone')
        : newCount === 1
        ? t('ach.gallery.refreshNew_one')
        : t('ach.gallery.refreshNew_other', { n: newCount }),
      'success',
    );
  }

  // Group earned thresholds per kind for fast lookup
  const earnedByKind = useMemo(() => {
    const map: Record<AchievementKind, Map<number, AchievementRow>> = {
      streak: new Map(),
      clients: new Map(),
      cuts: new Map(),
      revenue: new Map(),
      lessons: new Map(),
      transformations: new Map(),
      timekeeper: new Map(),
    };
    for (const row of earned) {
      map[row.kind].set(row.threshold, row);
    }
    return map;
  }, [earned]);

  const totalUnlocked = earned.length;
  const nick = useMemo(() => deriveNickname(earned), [earned]);

  // Subtitle: lists the categories that earned the title. Empty for newbie tier.
  const earnedCategoryNames = nick.earnedFrom.map((k) => t(`ach.kind.${k}`));
  const hasAnyElite = KIND_ORDER.some((k) => nick.tiers[k] === 'elite');
  const subtitleKey =
    nick.earnedFrom.length === 0
      ? 'nickname.subtitleNone'
      : hasAnyElite
      ? 'nickname.subtitle'
      : 'nickname.subtitleStrong';

  return (
    <div className="max-w-page mx-auto p-4 md:p-6 space-y-6 pb-24">
      <header className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-4xl md:text-[42px] text-navy-900 dark:text-gold-500 leading-[1.05]">
            {t('ach.gallery.title')}
          </h1>
          <p className="text-[15px] text-ink-500 mt-1.5">{t('ach.gallery.sub')}</p>
          {totalUnlocked > 0 && (
            <p className="text-[13px] text-ink-500 mt-2">
              {totalUnlocked === 1
                ? t('ach.gallery.unlocked_one')
                : t('ach.gallery.unlocked_other', { n: totalUnlocked })}
            </p>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {/* (Expand/collapse-all toggle removed — each kind section has its
              own dropdown and the controls were redundant.) */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing || all.loading}
            title={t('ach.gallery.refreshTitle')}
            aria-label={t('ach.gallery.refresh')}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-line bg-surface text-xs font-semibold text-ink-700 hover:border-navy-900/40 dark:hover:border-gold-500/40 disabled:opacity-50"
          >
            <RefreshIcon className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">
              {refreshing ? t('ach.gallery.refreshing') : t('ach.gallery.refresh')}
            </span>
          </button>
        </div>
      </header>

      {/* Nickname hero — derived purely from earned achievements. When the
          current nickname has matching artwork, render it as a side portrait
          on the right with the text on the left; the gradient stays as a
          fallback for nicknames that don't have art yet. */}
      <NicknameHero
        nicknameKey={nick.key}
        title={t(nick.key)}
        subtitle={t(subtitleKey, {
          categories:
            earnedCategoryNames.length === 0
              ? ''
              : earnedCategoryNames.length === 1
              ? earnedCategoryNames[0]
              : earnedCategoryNames.length === 2
              ? earnedCategoryNames.join(' & ')
              : earnedCategoryNames.slice(0, -1).join(', ') + ' & ' + earnedCategoryNames.slice(-1),
        })}
        eyebrow={t('nickname.eyebrow')}
      />

      <LeaderboardPanel />

      {/* (NicknameJourneySection used to live here as its own collapsible
          panel. It's now embedded inside the NicknameHero above — one
          tap on the hero header opens the journey list inline.) */}

      {KIND_ORDER.map((kind) => {
        const catalog = ACHIEVEMENT_CATALOG[kind];
        const earnedMap = earnedByKind[kind];
        const tierUnlockedCount = catalog.tiers.map(
          (tier) => tier.filter((th) => earnedMap.has(th)).length,
        );
        const totalForKind = catalog.thresholds.length; // always 8
        const unlockedForKind = earnedMap.size;
        const isOpen = expandedKinds.has(kind);
        return (
          <section key={kind} className="space-y-4">
            {/* Section header is the collapse control. Whole row is the
                tap target so missing the chevron on a phone still works.
                Counts + a thin rail give a peek at progress when collapsed
                so the user can decide whether expanding is worth it. */}
            <button
              type="button"
              onClick={() => toggleKind(kind)}
              aria-expanded={isOpen}
              aria-controls={`ach-section-${kind}`}
              className="w-full flex items-center gap-3 text-left rounded-xl border border-line bg-surface px-3 py-2.5 hover:border-navy-900/30 dark:hover:border-gold-500/30 transition-colors"
            >
              <KindIcon kind={kind} className="w-4 h-4 text-navy-900 dark:text-gold-400" />
              <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-700 dark:text-ink-700">
                {t(`ach.kind.${kind}`)}
              </span>
              <span className="ml-auto flex items-center gap-2.5">
                <span className="text-[11px] font-semibold text-ink-500 tabular-nums">
                  {t('ach.gallery.kindCount', { n: unlockedForKind, total: totalForKind })}
                </span>
                <span className="hidden sm:block w-16 h-1 rounded-full bg-line overflow-hidden">
                  <span
                    className="block h-full bg-gold-500"
                    style={{ width: `${(unlockedForKind / totalForKind) * 100}%` }}
                  />
                </span>
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 text-ink-500 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                />
              </span>
            </button>
            {!isOpen ? null : (
              <div id={`ach-section-${kind}`} className="space-y-4">
            {kind === 'streak' && <StreakProtectionCard />}
            {catalog.tiers.map((tier, tierIdx) => {
              const tierComplete = tierUnlockedCount[tierIdx] === tier.length;
              const tierStarted = (tierUnlockedCount[tierIdx] ?? 0) > 0;
              return (
                <div key={tierIdx} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] tracking-[0.16em] uppercase font-extrabold px-2 py-0.5 rounded-full ${
                        tierComplete
                          ? 'bg-gold-500 text-navy-900'
                          : tierStarted
                          ? 'bg-navy-900/8 dark:bg-gold-500/15 text-navy-900 dark:text-gold-400'
                          : 'bg-bg-alt text-ink-500 border border-line'
                      }`}
                    >
                      {t('ach.tier', { n: tierIdx + 1 })}
                    </span>
                    {tierComplete && (
                      <span className="text-[10px] uppercase font-extrabold tracking-[0.14em] text-gold-500">
                        {t('ach.tierComplete')}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {tier.map((threshold) => {
                      const earnedRow = earnedMap.get(threshold);
                      const unlocked = !!earnedRow;
                      const thr = thresholdLabel(kind, threshold);
                      const label = t(thr.key, thr.vars);
                      const artwork = unlocked ? badgeArtworkUrl(kind, threshold) : null;
                      // Locked chips show a progress bar from the previous
                      // threshold up to this one — so the chip the user is
                      // currently working toward shows partial fill, future
                      // chips show empty bars at a glance.
                      const bandFill = unlocked ? 1 : progressInBand(kind, threshold, currentByKind[kind]);

                      // Three render paths:
                      //   (1) unlocked + artwork → full-bleed image with bottom-fade label
                      //   (2) unlocked + no artwork → gradient + KindIcon (legacy fallback)
                      //   (3) locked → muted bg + lock icon + progress bar
                      if (unlocked && artwork) {
                        return (
                          <button
                            key={threshold}
                            type="button"
                            onClick={() => setDetail(earnedRow)}
                            aria-label={t('ach.detail.openAria', { milestone: label })}
                            className="relative rounded-2xl aspect-square overflow-hidden text-white shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
                          >
                            <img
                              src={artwork}
                              alt=""
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            {/* Bottom gradient + label so the threshold stays
                                readable without dominating the artwork. */}
                            <div className="absolute inset-x-0 bottom-0 px-3 pt-6 pb-2 bg-gradient-to-t from-black/85 via-black/55 to-transparent">
                              <div className="text-[12px] font-bold leading-tight text-white drop-shadow">
                                {label}
                              </div>
                            </div>
                            {/* Tiny check chip in the top-right corner. */}
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/95 text-navy-900 grid place-items-center shadow">
                              <CheckIcon className="w-3 h-3" />
                            </div>
                          </button>
                        );
                      }

                      const tileClasses = `relative rounded-2xl p-4 aspect-square flex flex-col items-center justify-center text-center transition-all ${
                        unlocked
                          ? `${catalog.gradient} text-white shadow-md hover:shadow-lg active:scale-[0.98]`
                          : 'bg-bg-alt border border-line text-ink-500 opacity-70'
                      }`;
                      const inner = (
                        <>
                          <div
                            className={`w-12 h-12 rounded-xl grid place-items-center mb-2 ${
                              unlocked ? 'bg-white/15 ' + catalog.iconColor : 'bg-surface text-ink-500'
                            }`}
                          >
                            <KindIcon kind={kind} className="w-6 h-6" />
                          </div>
                          <div className={`text-sm font-bold leading-tight ${unlocked ? '' : 'text-ink-700'}`}>
                            {label}
                          </div>
                          {unlocked ? (
                            <div className="text-[10px] tracking-wide uppercase font-extrabold text-white/80 mt-1.5">
                              <CheckIcon className="w-3.5 h-3.5 inline-block" />
                            </div>
                          ) : (
                            <>
                              <LockIcon className="w-3.5 h-3.5 mt-2 opacity-50" />
                              <div className="absolute left-3 right-3 bottom-2.5 h-1 rounded-full bg-line/70 overflow-hidden">
                                <div
                                  className="h-full bg-gold-500 transition-[width] duration-500"
                                  style={{ width: `${Math.round(bandFill * 100)}%` }}
                                  aria-hidden
                                />
                              </div>
                            </>
                          )}
                        </>
                      );
                      if (unlocked) {
                        return (
                          <button
                            key={threshold}
                            type="button"
                            onClick={() => setDetail(earnedRow)}
                            aria-label={t('ach.detail.openAria', { milestone: label })}
                            className={tileClasses}
                          >
                            {inner}
                          </button>
                        );
                      }
                      return (
                        <div
                          key={threshold}
                          className={tileClasses}
                          role="img"
                          aria-label={t('ach.gallery.lockedAria', {
                            milestone: label,
                            pct: Math.round(bandFill * 100),
                          })}
                        >
                          {inner}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
              </div>
            )}
          </section>
        );
      })}

      {detail && (
        <AchievementDetailModal achievement={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

function NicknameHero({
  nicknameKey,
  title,
  subtitle,
  eyebrow,
}: {
  nicknameKey: string;
  title: string;
  subtitle: string;
  eyebrow: string;
}) {
  const t = useT();
  const artwork = nicknameArtworkUrl(nicknameKey);
  const baseGradient =
    'radial-gradient(ellipse at 78% 25%, rgba(212,169,66,.20), transparent 55%), radial-gradient(ellipse at 18% 85%, rgba(200,16,46,.18), transparent 55%), linear-gradient(135deg, #0d1424 0%, #1a2954 100%)';
  // The hero now owns the Nickname-Journey expand/collapse state. The
  // CTA span from the old design ("See your title journey") is gone —
  // a chevron at the top-right of the gradient panel is the dropdown
  // affordance, and the journey list renders inline below the title
  // when expanded. One component, one tap.
  const [journeyOpen, setJourneyOpen] = useState(false);
  function toggleJourney() {
    setJourneyOpen((v) => !v);
  }
  const ariaToggleLabel = t(
    journeyOpen ? 'nickname.hero.collapseAria' : 'nickname.hero.expandAria',
    { title },
  );

  // Content shape changes slightly depending on whether the user's
  // nickname has matching artwork. Both shapes share the gradient
  // wrapper + the embedded journey list below.
  const headerWithArt = (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 md:gap-4">
      <div className="p-7 md:p-9 pr-3 min-w-0">
        <NicknameHeroEyebrow eyebrow={eyebrow} />
        <h2 className="font-display text-[36px] md:text-[52px] leading-[1.05] text-white mb-3">
          {title}
        </h2>
        <p className="text-white/72 text-[14px] md:text-[15px] leading-relaxed max-w-md">
          {subtitle}
        </p>
      </div>
      {/* Side portrait — square, matches the hero's height. */}
      <div className="relative shrink-0 self-stretch">
        <img
          src={artwork ?? ''}
          alt=""
          className="h-full w-32 sm:w-40 md:w-56 object-cover object-center"
          loading="eager"
        />
        <div
          className="absolute inset-y-0 left-0 w-8 md:w-12 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, rgba(13,20,36,1), rgba(13,20,36,0))',
          }}
          aria-hidden
        />
      </div>
    </div>
  );
  const headerNoArt = (
    <div className="p-7 md:p-9 pr-12 md:pr-14">
      <NicknameHeroEyebrow eyebrow={eyebrow} />
      <h2 className="font-display text-[40px] md:text-[56px] leading-[1.05] text-white mb-3">
        {title}
      </h2>
      <p className="text-white/72 text-[15px] leading-relaxed max-w-xl">{subtitle}</p>
    </div>
  );

  return (
    <section
      className="relative rounded-3xl overflow-hidden text-white shadow-md"
      style={{ background: baseGradient }}
    >
      {/* The whole hero panel is the toggle target — tap anywhere to
          open the embedded journey list. The chevron sits in the
          top-right as the visual affordance. */}
      <button
        type="button"
        onClick={toggleJourney}
        aria-expanded={journeyOpen}
        aria-label={ariaToggleLabel}
        className="block w-full text-left hover:opacity-95 active:opacity-90 transition-opacity"
      >
        {artwork ? headerWithArt : headerNoArt}
        {/* Chevron dropdown affordance, top-right */}
        <span className="absolute top-4 right-4 md:top-5 md:right-5 w-9 h-9 rounded-full bg-white/12 hover:bg-white/18 grid place-items-center text-gold-400">
          <ChevronDownIcon
            className={`w-4 h-4 transition-transform ${journeyOpen ? '' : '-rotate-90'}`}
          />
        </span>
      </button>

      {journeyOpen && (
        <div className="border-t border-white/15 bg-navy-900/40 px-5 py-4 md:px-7 md:py-5">
          <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-400 mb-3">
            {t('nickname.journey.title')}
          </div>
          <NicknameJourneyList />
        </div>
      )}
    </section>
  );
}

function NicknameHeroEyebrow({ eyebrow }: { eyebrow: string }) {
  return (
    <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-400 mb-2">
      {eyebrow}
    </div>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

// Streak protection card — surfaces both the cooloff bank and shield
// counts on the Achievements page with a brief plain-English explainer
// and "next earned in N alive days" countdowns. Mirrors the chips on
// the Today ribbon but in a more permanent, expanded layout since this
// is the screen barbers visit when they're thinking about the system.
function StreakProtectionCard() {
  const streak = useCurrentStreak();
  const shields = useStreakShields();
  const t = useT();

  const COOLOFF_MAX = 3;
  const COOLOFF_INTERVAL = 3;
  const SHIELD_MAX = 6;
  const SHIELD_INTERVAL = 100;

  const aliveDays = streak.data?.alive_day_count ?? 0;
  const cooloffs = streak.data?.cooloffs_available ?? 0;
  const shieldCount = shields.data?.length ?? 0;
  const cooloffAtCap = cooloffs >= COOLOFF_MAX;
  const cooloffNextIn = cooloffAtCap
    ? null
    : COOLOFF_INTERVAL - (aliveDays % COOLOFF_INTERVAL) || COOLOFF_INTERVAL;
  const shieldAtCap = shieldCount >= SHIELD_MAX;
  const shieldNextIn = shieldAtCap
    ? null
    : SHIELD_INTERVAL - (aliveDays % SHIELD_INTERVAL) || SHIELD_INTERVAL;

  return (
    // Inlined inside the Streak achievement dropdown so the protection
    // status sits next to the streak badges. No outer panel chrome —
    // the parent section already has the heading + boundary.
    <div className="space-y-2">
      <p className="text-[12px] text-ink-500 leading-relaxed">
        {t('achievements.protection.lede')}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* Cooloff bank */}
        <div className="rounded-xl border border-line bg-bg-alt p-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <SnowflakeProtIcon className="w-5 h-5 text-navy-900 dark:text-gold-500 shrink-0" />
            <strong className="text-sm">{t('today.streak.cooloff.title')}</strong>
            <span className="ml-auto text-[12px] tabular-nums font-extrabold">
              {t('today.streak.cooloff.banked', { count: cooloffs, max: COOLOFF_MAX })}
            </span>
          </div>
          <p className="text-[12px] text-ink-500 leading-snug">
            {t('today.streak.cooloff.body')}
          </p>
          <p className="text-[12px] font-bold">
            {cooloffNextIn == null
              ? t('today.streak.cooloff.atCap')
              : t('today.streak.cooloff.nextIn', { days: cooloffNextIn })}
          </p>
        </div>

        {/* Shields */}
        <div className="rounded-xl border border-line bg-bg-alt p-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <ShieldProtIcon className="w-5 h-5 text-navy-900 dark:text-gold-500 shrink-0" />
            <strong className="text-sm">{t('today.streak.shield.title')}</strong>
            <span className="ml-auto text-[12px] tabular-nums font-extrabold">
              {t('today.streak.shield.banked', { count: shieldCount, max: SHIELD_MAX })}
            </span>
          </div>
          <p className="text-[12px] text-ink-500 leading-snug">
            {t('today.streak.shield.body')}
          </p>
          <p className="text-[12px] font-bold">
            {shieldNextIn == null
              ? t('today.streak.shield.atCap')
              : t('today.streak.shield.nextIn', { days: shieldNextIn })}
          </p>
        </div>
      </div>
    </div>
  );
}

function SnowflakeProtIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="m20 16-4-4 4-4" />
      <path d="m4 8 4 4-4 4" />
      <path d="m16 4-4 4-4-4" />
      <path d="m8 20 4-4 4 4" />
    </svg>
  );
}

function ShieldProtIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}
